import { Flex, Text } from "@mantine/core";
import { useIntl } from "react-intl";
import { i18n } from "../i18n/langType";
import { TariffDetailNode } from "../types/types";

interface InterimBlockingFeeDisplayProps {
  tariffNode: TariffDetailNode | null | undefined;
  currency: string;
}

export function InterimBlockingFeeDisplay({ tariffNode, currency }: InterimBlockingFeeDisplayProps) {
  const intl = useIntl();

  // Early return if no tariff data
  if (!tariffNode || !tariffNode.prices || tariffNode.prices.length === 0) {
    return (
      <Text size="xl" fw="600">
        {intl.formatNumber(0, {
          style: "currency",
          currency: currency,
        })}
      </Text>
    );
  }

  // Handle constant tariff (single price)
  if (tariffNode.price_type === "ConstantTariffElement") {
    const price = tariffNode.prices[0]?.price || 0;
    
    if (price === 0) {
      return (
        <Text size="xl" fw="600">
          {intl.formatNumber(0, {
            style: "currency",
            currency: currency,
          })}
        </Text>
      );
    }
    
    return (
      <Text size="xl" fw="600">
        {intl.formatNumber(Number(price), {
          style: "currency",
          currency: currency,
        })}
      </Text>
    );
  }

  // Handle variable tariff
  if (tariffNode.price_type === "VariableTariffElement") {
    const prices = tariffNode.prices;
    const freePrice = prices.find(p => p.price === 0);
    const paidPrice = prices.find(p =>p.price != null && p.price > 0);

    // If there's a free period and a paid period
    if (freePrice && paidPrice && paidPrice.min) {
      const thresholdMinutes = paidPrice.min;
      
      return (
        <Flex direction="column" align="center">
          <Text size="xl" fw="600">
            {intl.formatNumber(Number(paidPrice.price), {
              style: "currency",
              currency: currency,
            })}/{intl.formatMessage({ id: i18n.minuteAbbr })}
          </Text>
          <Text size="xs" color="gray.6" style={{ textAlign: "center" }}>
            {intl.formatMessage(
                { id: i18n.interimBlockingFeeTemplate },
                { thresholdMinutes: thresholdMinutes }
            )}
          </Text>
        </Flex>
      );
    }

    // If only one price in variable tariff
    const firstPrice = prices[0];
    if (firstPrice) {
      return (
        <Text size="xl" fw="600">
          {intl.formatNumber(Number(firstPrice.price), {
            style: "currency",
            currency: currency,
          })}
        </Text>
      );
    }
  }

  // Fallback
  return (
    <Text size="xl" fw="600">
      {intl.formatNumber(0, {
        style: "currency",
        currency: currency,
      })}
    </Text>
  );
}